/**
 * Zeffy's hosted script only runs on DOMContentLoaded and scans for [zeffy-form-link]
 * immediately. In a React SPA, those nodes are not in the DOM yet, so the script
 * never binds and <a href> navigates away. This module mirrors Zeffy's modal behavior
 * and attaches after React has rendered (see App useEffect).
 */

const ZEFFY_ATTR = 'zeffy-form-link';
const ZEFFY_MSG_ID = 'zeffy-iframe';

function withCacheBust(url: string): string {
  const u = new URL(url);
  u.searchParams.set('cachebust', String(Date.now()));
  return u.toString();
}

function applyPanelLayout(panel: HTMLElement, mq: MediaQueryList): void {
  if (mq.matches) {
    panel.style.top = '5%';
    panel.style.bottom = '5%';
    panel.style.left = '10%';
    panel.style.right = '10%';
  } else {
    panel.style.top = '0';
    panel.style.bottom = '0';
    panel.style.left = '0';
    panel.style.right = '0';
  }
}

export function attachZeffyEmbedForms(): () => void {
  document
    .querySelectorAll<HTMLIFrameElement>(
      'iframe[src^="https://www.zeffy.com/"], iframe[src^="https://app.simplyk.io/"]',
    )
    .forEach((frame) => {
      try {
        frame.src = withCacheBust(frame.src);
      } catch {
        /* ignore invalid src */
      }
    });

  const backdrop = document.createElement('div');
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.style.cssText =
    'display:none;position:fixed;inset:0;background-color:rgba(0,0,0,0.5);z-index:10000;transition:opacity 0.25s ease-in-out;opacity:0;';

  const panel = document.createElement('div');
  panel.setAttribute('aria-hidden', 'true');
  panel.style.cssText =
    'display:none;position:fixed;top:0;bottom:0;right:0;left:0;z-index:10001;transition:opacity 0.25s ease-in-out;opacity:0;';

  const iframe = document.createElement('iframe');
  iframe.title = 'Form powered and secured by Zeffy';
  iframe.style.cssText = 'width:100%;height:100%;border:none;border-radius:4px;';
  iframe.setAttribute('allow', 'payment');

  panel.appendChild(iframe);
  document.body.append(backdrop, panel);

  const mq = window.matchMedia('(min-width: 769px)');
  const onMq = () => applyPanelLayout(panel, mq);
  mq.addEventListener('change', onMq);
  onMq();

  const show = (): void => {
    backdrop.style.display = 'block';
    backdrop.setAttribute('aria-hidden', 'false');
    panel.style.display = 'block';
    panel.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => {
      backdrop.style.opacity = '1';
      panel.style.opacity = '1';
    });
    window.setTimeout(() => {
      iframe.contentWindow?.postMessage({ id: ZEFFY_MSG_ID, open: true }, '*');
    }, 0);
  };

  const hide = (): void => {
    backdrop.style.opacity = '0';
    panel.style.opacity = '0';
    window.setTimeout(() => {
      backdrop.style.display = 'none';
      backdrop.setAttribute('aria-hidden', 'true');
      panel.style.display = 'none';
      panel.setAttribute('aria-hidden', 'true');
    }, 250);
  };

  const onBackdropClick = () => hide();
  backdrop.addEventListener('click', onBackdropClick);

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') hide();
  };
  window.addEventListener('keydown', onKey);

  const onMessage = (ev: MessageEvent): void => {
    if (!ev.isTrusted || !ev.data || ev.data.id !== ZEFFY_MSG_ID || !ev.data.close) return;
    hide();
  };
  window.addEventListener('message', onMessage);

  const onDocClick = (ev: MouseEvent): void => {
    const t = ev.target;
    if (!(t instanceof Element)) return;
    const el = t.closest(`[${ZEFFY_ATTR}]`);
    if (!el) return;
    const url = el.getAttribute(ZEFFY_ATTR);
    if (!url) return;
    ev.preventDefault();
    iframe.src = withCacheBust(url);
    show();
  };

  document.addEventListener('click', onDocClick, true);

  return () => {
    document.removeEventListener('click', onDocClick, true);
    backdrop.removeEventListener('click', onBackdropClick);
    mq.removeEventListener('change', onMq);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('message', onMessage);
    backdrop.remove();
    panel.remove();
  };
}
